import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
    parse,
    format,
    parseISO,
    addDays,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
} from "date-fns";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Button, Input } from "../../../shared/ui/FormElements";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { useAuth } from "../../../../context/AuthContext";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";

import { DandvatService } from "../../../services/DandvatService";
import { DandvatRow, DandvatsHistoryModel, SaveDandvatsModel } from "../../../models/DandvatsModel";

type Nav = NativeStackNavigationProp<AuthStackParamList>;
type DandvatHistoryRouteProp = RouteProp<AuthStackParamList, "DandvatsHistory">;

type HistoryFilter = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";

function OutlineButton({
    title,
    onPress,
    style,
}: {
    title: string;
    onPress: () => void;
    style?: any;
}) {
    return (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [
                styles.outlineBtn,
                pressed ? { opacity: 0.85 } : null,
                style,
            ]}
        >
            <Text style={styles.outlineText}>{title}</Text>
        </Pressable>
    );
}

function toYmd(d: Date) {
    return format(d, "yyyy-MM-dd");
}

function parseHistoryInputDate(value: string) {
    const trimmed = String(value || "").trim();

    const isoParsed = parse(trimmed, "yyyy-MM-dd", new Date());
    if (!Number.isNaN(isoParsed.getTime())) return isoParsed;

    const fallback = new Date(trimmed);
    if (!Number.isNaN(fallback.getTime())) return fallback;

    return new Date();
}

export function DandvatHistoryScreen() {
    useNavigation<Nav>();
    const route = useRoute<DandvatHistoryRouteProp>();
    const { user } = useAuth();
    const initialRange = resolveHistoryRange(route.params);

    // ✅ Use actual userId (Guid) from auth
    const userId: string = (user as any)?.userId || (user as any)?.raw?.userId || "";

    const [filterType, setFilterType] = useState<HistoryFilter>(
        initialRange.hasNavigationDates ? "Custom" : "Monthly"
    );

    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [startDate, setStartDate] = useState(initialRange.fromDate);
    const [endDate, setEndDate] = useState(initialRange.toDate);

    const [summary, setSummary] = useState({ isSelf: true });


    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [entries, setEntries] = useState<DandvatRow[]>([]);

    const [editing, setEditing] = useState<SaveDandvatsModel | null>(null);
    const [editCount, setEditCount] = useState("");
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const filters: HistoryFilter[] = useMemo(
        () => ["Weekly", "Monthly", "Quarterly", "Yearly"],
        []
    );

    const loadHistory = async (uid: string, s: string, e: string) => {
        if (!uid) return;

        // ✅ Backend condition: d.Date >= FromDate AND d.Date < ToDate
        // so ToDate must be endDate + 1 day to include endDate itself.
        if (!effectiveUserId) return;
        const toExclusive = toYmd(addDays(parseISO(e), 1));
        const payload: DandvatsHistoryModel = {
            userId: effectiveUserId,
            FromDate: s,
            ToDate: toExclusive
        };
        setLoading(true);
        try {
            const res = await DandvatService.getDandvatHistory(payload);

            if (res?.status !== 200) {
                setEntries([]);
                return;
            }

            const list = (res?.data.records ?? []).map((x: any) => ({
                dandvatId: String(x.dandvatId ?? x.DandvatId ?? ""),
                userId: String(x.userId ?? x.UserId ?? ""),
                date: String(x.date ?? x.Date ?? ""),
                count: Number(x.count ?? x.Count ?? 0),
                marks: Number(x.marks ?? x.Marks ?? 0),
                isEditable: x.isEditable
            })) as DandvatRow[];

            setEntries(list);
            setSummary(res.data.response);

        } catch {
            setEntries([]);
        } finally {
            setLoading(false);
        }
    };

    // ✅ initial load
    useEffect(() => {
        if (!userId) return;
        loadHistory(userId, startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // ✅ When filter changes, you can compute start/end here if you want.
    // For now, your UI uses custom dates anyway.
    useEffect(() => {
        if (!userId) return;
        loadHistory(userId, startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, startDate, endDate]);

    const onFilterPress = (t: HistoryFilter) => {
        setFilterType(t);

        const today = new Date();
        let nextStart = today;
        let nextEnd = today;

        switch (t) {
            case "Weekly":
                nextStart = startOfWeek(today);
                nextEnd = endOfWeek(today);
                break;
            case "Monthly":
                nextStart = startOfMonth(today);
                nextEnd = endOfMonth(today);
                break;
            case "Quarterly":
                nextStart = startOfQuarter(today);
                nextEnd = endOfQuarter(today);
                break;
            case "Yearly":
                nextStart = startOfYear(today);
                nextEnd = endOfYear(today);
                break;
            default:
                return;
        }

        setStartDate(format(nextStart, "yyyy-MM-dd"));
        setEndDate(format(nextEnd, "yyyy-MM-dd"));
    };

    const onStartChange = (t: string) => {
        setStartDate(t.trim());
        setFilterType("Custom");
    };

    const onEndChange = (t: string) => {
        setEndDate(t.trim());
        setFilterType("Custom");
    };

    const openDatePicker = (mode: "start" | "end") => {
        const currentValue = mode === "start" ? startDate : endDate;

        DateTimePickerAndroid.open({
            value: parseHistoryInputDate(currentValue),
            mode: "date",
            onChange: (_, selectedDate) => {
                if (!selectedDate) return;

                const formatted = format(selectedDate, "yyyy-MM-dd");
                if (mode === "start") onStartChange(formatted);
                else onEndChange(formatted);
            },
        });
    };

    const del = (dandvatId: string) => {
        setPendingDeleteId(dandvatId);
    };

    const openEdit = async (row: DandvatRow) => {
        try {
            setLoading(true);
            const res = await DandvatService.getDandvat(row.dandvatId);

            if (res?.status !== 200) return;

            // Your API returns a list (LINQ .Select() without FirstOrDefault)
            const dtoRaw = Array.isArray(res?.data) ? res.data[0] : res?.data;

            const dto: SaveDandvatsModel = {
                userId: String(dtoRaw?.userId ?? dtoRaw?.UserId ?? userId),
                dandvatId: String(dtoRaw?.dandvatId ?? dtoRaw?.DandvatId ?? row.dandvatId),
                date: String(dtoRaw?.date ?? dtoRaw?.Date ?? row.date),
                count: Number(dtoRaw?.count ?? dtoRaw?.Count ?? row.count),
                marks: Number(dtoRaw?.marks ?? dtoRaw?.Marks ?? row.marks),
            };

            setEditing(dto);
            setEditCount(String(dto.count));
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    const saveEdit = async () => {
        if (!editing) return;

        const countNum = parseInt(editCount, 10);
        if (!countNum || countNum < 0) {
            Alert.alert("Required", "Enter valid number of Dandvats");
            return;
        }

        const updated: SaveDandvatsModel = {
            ...editing,
            count: countNum,
            marks: Number((countNum / 3).toFixed(2)), // ✅ 0.33, 0.67, 1.00
        };

        try {
            setLoading(true);
            const res = await DandvatService.saveDandvat(updated);
            if (res?.status !== 200) {
                Alert.alert("Error", res?.message ?? "Failed to update");
                return;
            }
            setEditing(null);
            await loadHistory(userId, startDate, endDate);
        } finally {
            setLoading(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadHistory(userId, startDate, endDate);
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <DivineBackground overlayOpacity={0.95}>
            <Header title="History & Analytics" showBack showHome showProfile />

            <ScrollView
                contentContainerStyle={styles.scroll}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        colors={["#F97316"]}
                        tintColor="#F97316"
                    />
                }
            >
                {/* Filter Card */}
                <View style={styles.filterCard}>
                    <View style={styles.filterHead}>
                        <Text>🔎 </Text>
                        <Text style={styles.filterTitle}>Filter History</Text>
                    </View>

                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterRow}>
                        {filters.map((t) => {
                            const active = filterType === t;
                            return (
                                <Pressable
                                    key={t}
                                    onPress={() => onFilterPress(t)}
                                    style={[styles.pill, active ? styles.pillOn : styles.pillOff]}
                                >
                                    <Text style={[styles.pillText, active ? styles.pillTextOn : styles.pillTextOff]}>
                                        {t}
                                    </Text>
                                </Pressable>
                            );
                        })}
                    </ScrollView>

                    <View style={styles.dateGrid}>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>From Date</Text>
                            <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("start")}>
                                <Text style={styles.dateIcon}>📅</Text>
                                <Text style={styles.dateValue}>{startDate || "yyyy-mm-dd"}</Text>
                            </TouchableOpacity>
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.dateLabel}>To Date</Text>
                            <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("end")}>
                                <Text style={styles.dateIcon}>📅</Text>
                                <Text style={styles.dateValue}>{endDate || "yyyy-mm-dd"}</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>

                {/* List */}
                <View style={{ gap: 10 }}>
                    {loading ? (
                        <View style={styles.empty}>
                            <Text style={styles.emptyText}>Loading...</Text>
                        </View>
                    ) : entries.length === 0 ? (
                        <View style={styles.empty}>
                            <Text style={styles.emptyText}>No records found for this period.</Text>
                        </View>
                    ) : (
                        entries.map((e) => (
                            <View key={e.dandvatId} style={styles.item}>
                                <View>
                                    <Text style={styles.itemDate}>
                                        {format(parseISO(e.date), "dd MMM yyyy")}
                                    </Text>
                                    <Text style={styles.itemDay}>
                                        {format(parseISO(e.date), "EEEE")}
                                    </Text>
                                </View>

                                <View style={styles.itemRight}>
                                    <View style={{ alignItems: "flex-end" }}>
                                        <Text style={styles.itemNumOrange}>{e.count}</Text>
                                        <Text style={styles.itemSmall}>Dandvats</Text>
                                    </View>

                                    <View style={{ alignItems: "flex-end", width: 62 }}>
                                        <Text style={styles.itemNumAmber}>{e.marks}</Text>
                                        <Text style={styles.itemSmall}>Marks</Text>
                                    </View>


                                    {summary.isSelf && (
                                        <View style={styles.itemBtns}>
                                            {e.isEditable ? (
                                                <Pressable onPress={() => openEdit(e)} style={styles.iconBox}>
                                                    <Text>✏️</Text>
                                                </Pressable>
                                            ) : (
                                                <View style={[styles.iconBox, styles.iconBoxPlaceholder]} />
                                            )}
                                            <Pressable onPress={() => del(e.dandvatId)} style={[styles.iconBox, styles.iconBoxRed]}>
                                                <Text style={[styles.iconBtnText, { color: "#DC2626" }]}>🗑</Text>
                                            </Pressable>
                                        </View>
                                    )}
                                </View>
                            </View>
                        ))
                    )}
                </View>
            </ScrollView>

            {/* Edit Modal */}
            <Modal
                visible={!!editing}
                transparent
                animationType="fade"
                onRequestClose={() => setEditing(null)}
            >
                <Pressable style={styles.modalBackdrop} onPress={() => setEditing(null)} />

                <View style={styles.modalCard}>
                    <View style={styles.modalHead}>
                        <Text style={styles.modalTitle}>Edit Entry</Text>
                        <Pressable onPress={() => setEditing(null)} hitSlop={12}>
                            <Text style={styles.modalX}>×</Text>
                        </Pressable>
                    </View>

                    {editing ? (
                        <>
                            <Text style={styles.modalSub}>
                                Date: {format(parseISO(editing.date), "dd MMMM yyyy")}
                            </Text>

                            <Input
                                label="Number of Dandvats"
                                value={editCount}
                                keyboardType="number-pad"
                                onChangeText={(t) => setEditCount(t.replace(/\D/g, ""))}
                            />

                            <View style={{ height: 14 }} />

                            <View style={{ flexDirection: "row", gap: 10 }}>
                                <OutlineButton title="Cancel" onPress={() => setEditing(null)} style={{ flex: 1 }} />
                                <Button title="Update" onPress={saveEdit} style={{ flex: 1 }} />
                            </View>
                        </>
                    ) : null}
                </View>
            </Modal>

            <ConfirmDialog
                visible={!!pendingDeleteId}
                title="Delete"
                message="Are you sure you want to delete this entry?"
                confirmText="Delete"
                destructive
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={async () => {
                    if (!pendingDeleteId) return;
                    try {
                        await DandvatService.deleteDandvat(pendingDeleteId);
                        setPendingDeleteId(null);
                    } finally {
                        await loadHistory(userId, startDate, endDate);
                    }
                }}
            />
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    scroll: { padding: 16, paddingBottom: 140, gap: 14 },

    filterCard: {
        backgroundColor: "rgba(255,255,255,0.90)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
        shadowColor: "#000",
        shadowOpacity: 0.1,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 8 },
        elevation: 5,
    },
    filterHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
    filterTitle: { fontSize: 16, fontWeight: "900", color: "#111827" },

    filterRow: { gap: 8, paddingBottom: 6 },
    pill: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, borderWidth: 1 },
    pillOn: { backgroundColor: "rgba(249,115,22,0.95)", borderColor: "rgba(249,115,22,0.95)" },
    pillOff: { backgroundColor: "rgba(255,237,213,0.55)", borderColor: "rgba(251,146,60,0.20)" },
    pillText: { fontSize: 12, fontWeight: "900" },
    pillTextOn: { color: "#fff" },
    pillTextOff: { color: "#9A3412" },

    dateGrid: { flexDirection: "row", gap: 12, marginTop: 8 },
    dateLabel: { fontSize: 11, fontWeight: "800", color: "rgba(107,114,128,0.85)", marginLeft: 2, marginBottom: 4 },
    dateInput: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.10)",
        borderRadius: 10,
        backgroundColor: "rgba(255,255,255,0.75)",
        minHeight: 44,
        paddingHorizontal: 12,
    },
    dateIcon: {
        fontSize: 16,
    },
    dateValue: {
        fontSize: 14,
        fontWeight: "600",
        color: "#4A2311",
    },

    item: {
        backgroundColor: "rgba(255,255,255,0.92)",
        borderRadius: 16,
        padding: 14,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.12)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    itemDate: { fontSize: 12, fontWeight: "900", color: "#4A2311" },
    itemDay: { marginTop: 2, fontSize: 12, fontWeight: "700", color: "rgba(107,114,128,0.9)" },
    itemRight: { flexDirection: "row", alignItems: "center", gap: 8 },
    itemNumOrange: { fontSize: 18, fontWeight: "900", color: "#EA580C" },
    itemNumAmber: { fontSize: 18, fontWeight: "900", color: "#D97706" },
    itemSmall: { fontSize: 10, fontWeight: "800", color: "rgba(107,114,128,0.8)" },
    itemBtns: { flexDirection: "row", gap: 8 },
    iconBox: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: "rgba(255,237,213,0.60)",
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.18)",
        alignItems: "center",
        justifyContent: "center",
    },
    iconBoxRed: {
        backgroundColor: "rgba(254,226,226,0.70)",
        borderColor: "rgba(248,113,113,0.35)",
    },
    iconBoxPlaceholder: {
        opacity: 0,
    },
    iconBtnText: { fontSize: 14, fontWeight: "900", color: "#E11D48" },

    empty: {
        backgroundColor: "rgba(255,255,255,0.65)",
        borderRadius: 16,
        paddingVertical: 22,
        alignItems: "center",
    },
    emptyText: { fontSize: 12, fontWeight: "800", color: "rgba(107,114,128,0.9)" },

    modalBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: "rgba(0,0,0,0.45)" },
    modalCard: {
        position: "absolute",
        left: 18,
        right: 18,
        top: "30%",
        backgroundColor: "#fff",
        borderRadius: 18,
        padding: 16,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.22)",
    },
    modalHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
    modalTitle: { fontSize: 16, fontWeight: "900", color: "#111827" },
    modalX: { fontSize: 26, fontWeight: "900", color: "rgba(75,85,99,0.9)", marginTop: -6 },
    modalSub: { marginTop: 6, marginBottom: 10, fontSize: 12, fontWeight: "700", color: "rgba(107,114,128,0.9)" },

    outlineBtn: {
        height: 44,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: "rgba(156,163,175,0.65)",
        backgroundColor: "rgba(255,255,255,0.75)",
        alignItems: "center",
        justifyContent: "center",
    },
    outlineText: { fontSize: 13.5, fontWeight: "900", color: "rgba(75,85,99,0.95)" },
});
